import { writable, get } from "svelte/store";
import { api, ApiError } from "../api";
import { RealtimeConnection } from "../ws";
import * as idb from "../db/idb";
import type { OutboxEntry } from "../db/idb";
import type { GroceryItem, GroceryList, ChangeEvent } from "../types";
import { randomUUID } from "../uuid";

export const lists = writable<GroceryList[]>([]);
export const items = writable<GroceryItem[]>([]);
export const syncState = writable<"offline" | "syncing" | "synced">("offline");
export const itemSuggestions = writable<{ name: string; count: number }[]>([]);

const realtime = new RealtimeConnection();
let draining = false;
let started = false;

export async function initSync(): Promise<void> {
  if (started) return;
  started = true;

  lists.set(await idb.loadLists());
  items.set(await idb.loadItems());

  realtime.onOpen(() => {
    void runSync();
  });
  realtime.onMessage(applyServerEvent);
  realtime.connect();

  window.addEventListener("online", () => void runSync());

  void runSync();
}

export function stopSync(): void {
  realtime.disconnect();
  started = false;
}

async function runSync(): Promise<void> {
  syncState.set("syncing");
  try {
    await drainOutbox();
    await fetchDelta();
    syncState.set("synced");
  } catch {
    syncState.set("offline");
  }
  void loadSuggestions();
}

async function loadSuggestions(): Promise<void> {
  try {
    const { suggestions } = await api.getItemSuggestions();
    itemSuggestions.set(suggestions);
  } catch {
    // Best-effort UX enhancement — leave whatever suggestions we already have.
  }
}

async function fetchDelta(): Promise<void> {
  const meta = await idb.getMeta();
  const since = meta.lastSyncAt || undefined;

  const { lists: remoteLists } = await api.getLists(since);
  await mergeLists(remoteLists);

  const visibleLists = get(lists);
  for (const list of visibleLists) {
    const { items: remoteItems } = await api.getItems(list.id, since);
    await mergeItems(remoteItems);
  }

  await idb.setMeta({ ...(await idb.getMeta()), lastSyncAt: Date.now() });
}

async function mergeLists(remote: GroceryList[]): Promise<void> {
  if (remote.length === 0) return;
  const current = await idb.loadLists();
  const byId = new Map(current.map((l) => [l.id, l]));
  for (const list of remote) {
    if (list.deletedAt) {
      byId.delete(list.id);
    } else {
      byId.set(list.id, list);
    }
  }
  const next = [...byId.values()].sort((a, b) => a.createdAt - b.createdAt);
  await idb.saveLists(next);
  lists.set(next);
}

async function mergeItems(remote: GroceryItem[]): Promise<void> {
  if (remote.length === 0) return;
  const current = await idb.loadItems();
  const byId = new Map(current.map((i) => [i.id, i]));
  for (const item of remote) {
    if (item.deletedAt) {
      byId.delete(item.id);
    } else {
      byId.set(item.id, item);
    }
  }
  const next = [...byId.values()];
  await idb.saveItems(next);
  items.set(next);
}

function applyServerEvent(event: ChangeEvent): void {
  void (async () => {
    switch (event.type) {
      case "list.created":
      case "list.updated":
        await mergeLists([event.list]);
        break;
      case "list.deleted":
        await mergeLists([
          { id: event.listId, name: "", createdBy: "", createdAt: 0, updatedAt: Date.now(), deletedAt: Date.now() },
        ]);
        break;
      case "item.created":
      case "item.updated":
        await mergeItems([event.item]);
        break;
      case "item.deleted":
        await mergeItems([
          {
            id: event.itemId,
            listId: event.listId,
            name: "",
            quantity: null,
            unit: null,
            checked: false,
            position: 0,
            createdBy: "",
            updatedAt: Date.now(),
            deletedAt: Date.now(),
          },
        ]);
        break;
    }
  })();
}

async function drainOutbox(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    for (;;) {
      const outbox = await idb.loadOutbox();
      if (outbox.length === 0) return;
      const entry = outbox.reduce((a, b) => (a.seq < b.seq ? a : b));

      let done = false;
      try {
        await sendMutation(entry);
        done = true;
      } catch (err) {
        if (err instanceof ApiError) {
          // 4xx from the server: unrecoverable by retrying (bad request,
          // not-found, or already-superseded). Drop it and keep going.
          done = true;
        } else {
          // Network failure — stop draining, retry on next reconnect/online event.
          return;
        }
      }

      if (done) {
        const remaining = (await idb.loadOutbox()).filter((e) => e.seq !== entry.seq);
        await idb.saveOutbox(remaining);
      }
    }
  } finally {
    draining = false;
  }
}

async function sendMutation(entry: OutboxEntry): Promise<void> {
  switch (entry.op) {
    case "list.create": {
      const res = await api.createList(entry.payload.name as string);
      if (res.list.id !== entry.entityId) {
        await remapListId(entry.entityId, res.list.id);
      }
      await mergeLists([res.list]);
      return;
    }
    case "list.update": {
      const res = await api.renameList(entry.entityId, entry.payload.name as string);
      await mergeLists([res.list]);
      return;
    }
    case "list.delete": {
      await api.deleteList(entry.entityId);
      return;
    }
    case "list.clearChecked": {
      await api.clearChecked(entry.entityId);
      return;
    }
    case "item.create": {
      const res = await api.createItem(entry.listId!, {
        id: entry.entityId,
        name: entry.payload.name as string,
        quantity: (entry.payload.quantity as number | null) ?? null,
        unit: (entry.payload.unit as string | null) ?? null,
        position: entry.payload.position as number,
        updatedAt: entry.payload.updatedAt as number,
      });
      await mergeItems([res.item]);
      return;
    }
    case "item.update": {
      const res = await api.updateItem(entry.entityId, entry.payload);
      await mergeItems([res.item]);
      return;
    }
    case "item.delete": {
      await api.deleteItem(entry.entityId);
      return;
    }
  }
}

async function remapListId(tempId: string, realId: string): Promise<void> {
  const currentLists = await idb.loadLists();
  await idb.saveLists(currentLists.map((l) => (l.id === tempId ? { ...l, id: realId } : l)));

  const currentItems = await idb.loadItems();
  await idb.saveItems(
    currentItems.map((i) => (i.listId === tempId ? { ...i, listId: realId } : i)),
  );
  items.set(await idb.loadItems());

  const outbox = await idb.loadOutbox();
  await idb.saveOutbox(
    outbox.map((e) => ({
      ...e,
      entityId: e.entityId === tempId ? realId : e.entityId,
      listId: e.listId === tempId ? realId : e.listId,
    })),
  );
}

function triggerDrain(): void {
  void drainOutbox()
    .then(() => fetchDelta().catch(() => {}))
    .then(() => loadSuggestions());
}

export async function createList(name: string): Promise<GroceryList> {
  const id = `temp-${randomUUID()}`;
  const now = Date.now();
  const list: GroceryList = { id, name, createdBy: "", createdAt: now, updatedAt: now, deletedAt: null };

  const current = await idb.loadLists();
  const next = [...current, list];
  await idb.saveLists(next);
  lists.set(next);

  await idb.enqueue("list.create", id, { name });
  triggerDrain();
  return list;
}

export async function renameList(id: string, name: string): Promise<void> {
  const current = await idb.loadLists();
  const now = Date.now();
  const next = current.map((l) => (l.id === id ? { ...l, name, updatedAt: now } : l));
  await idb.saveLists(next);
  lists.set(next);

  await idb.enqueue("list.update", id, { name });
  triggerDrain();
}

export async function deleteList(id: string): Promise<void> {
  const current = await idb.loadLists();
  await idb.saveLists(current.filter((l) => l.id !== id));
  lists.set(await idb.loadLists());

  const currentItems = await idb.loadItems();
  await idb.saveItems(currentItems.filter((i) => i.listId !== id));
  items.set(await idb.loadItems());

  await idb.enqueue("list.delete", id, {});
  triggerDrain();
}

export async function clearChecked(listId: string): Promise<void> {
  const current = await idb.loadItems();
  const next = current.filter((i) => !(i.listId === listId && i.checked));
  await idb.saveItems(next);
  items.set(next);

  await idb.enqueue("list.clearChecked", listId, {});
  triggerDrain();
}

export async function createItem(
  listId: string,
  name: string,
  quantity: number | null = null,
  unit: string | null = null,
): Promise<void> {
  const id = randomUUID();
  const now = Date.now();
  const currentItems = await idb.loadItems();
  const maxPosition = currentItems
    .filter((i) => i.listId === listId)
    .reduce((max, i) => Math.max(max, i.position), 0);
  const item: GroceryItem = {
    id,
    listId,
    name,
    quantity,
    unit,
    checked: false,
    position: maxPosition + 1,
    createdBy: "",
    updatedAt: now,
    deletedAt: null,
  };

  const next = [...currentItems, item];
  await idb.saveItems(next);
  items.set(next);

  await idb.enqueue(
    "item.create",
    id,
    { name, quantity, unit, position: item.position, updatedAt: now },
    listId,
  );
  triggerDrain();
}

export async function updateItem(
  id: string,
  fields: Partial<{ name: string; quantity: number | null; unit: string | null; checked: boolean; position: number }>,
): Promise<void> {
  const now = Date.now();
  const current = await idb.loadItems();
  const next = current.map((i) => (i.id === id ? { ...i, ...fields, updatedAt: now } : i));
  await idb.saveItems(next);
  items.set(next);

  await idb.enqueue("item.update", id, { ...fields, updatedAt: now });
  triggerDrain();
}

export async function deleteItem(id: string): Promise<void> {
  const current = await idb.loadItems();
  await idb.saveItems(current.filter((i) => i.id !== id));
  items.set(await idb.loadItems());

  await idb.enqueue("item.delete", id, {});
  triggerDrain();
}
